import { SERVER_FORMAT_DATE } from '@/common/app/constants/timeConstants';
import { formatSubunits, getSavingsFromTickets } from '@/entities/Cart/app/utils/cartUtils';
import { IApiCartItem } from '@/entities/Cart/domain/Cart.domain';
import { OptionIcon } from '@/shared/images/icons/OptionIcon';
import { Ticket2 } from '@/shared/images/icons/Ticket2';
import { format, parse } from 'date-fns';
import { ReactElement, memo, useState, useId } from 'react';
import * as S from './CartItem.styles';
import { ResponseEditModal } from './ResponseEditModal/ResponseEditModal';
import { TripflexIcon } from '@/shared/images/icons/TripflexIcon';
import Link from 'next/link';
import URL from '@/common/app/utils/urlsUtils';
import { IActivity } from '@/entities/Activity/domain/Activity.domain';
import { ActivitySubtitle } from '@/screens/ActivityPage/ui/ActivitySubtitle/ActivitySubtitle';
import { Policy } from '@/shared/images/icons/Policy';
import { Tooltip } from '@/common/ui';

type CartItemProps = {
    item: IApiCartItem;
    isTripflex: boolean;
    activity?: IActivity;
    removeItem: (item: IApiCartItem) => void;
    editTicket: (item: IApiCartItem) => void;
};

const TRIPFLEX_ID = '1';
const TRIPFLEX_TITLE = 'TripFlex - Last minute cancellation coverage';
const TripflexTipContent = () => (
    <>
        <b>Cancel before your arrival time for ANY reason for a full refund*</b>
        <p>
            Reasons might include unfavorable weather, illness or injury, family emergency, heavy
            traffic or car trouble, or you just change your mind.
            <br />
            <br />
            *The TripFlex fee is non-refundable
        </p>
    </>
);

export const CartItemContainer = ({
    item,
    activity,
    removeItem,
    editTicket,
    isTripflex,
}: CartItemProps): ReactElement => {
    const arrivalDate =
        item.arrival_date &&
        format(parse(item.arrival_date, SERVER_FORMAT_DATE, 0), 'MMMM do, yyyy');

    const arrivalTime =
        item.product_id === '1453' ? 'All day admission' : item.schedule?.time?.toUpperCase();

    const itemTotals = getSavingsFromTickets(item.tickets, item.discount_rate_subunits);

    const [show, setShow] = useState(false);
    const keyId = useId();

    const displayModal = () => {
        setShow(true);
    };

    return (
        <S.Container>
            <S.TopWrapper data-test-id="Cart_trip_name">
                <S.TopContainer>
                    <S.ImageThumb>
                        <S.StyledImg
                            src={`${URL.image}/activity/${item.product_id}/center/240x240/ts`}
                            alt={item.product_name}
                        />
                    </S.ImageThumb>
                    <S.InnerWrap>
                        <Link
                            passHref
                            href={`/${item.product_slug}/details/${item.product_id}/`.toLowerCase()}
                        >
                            <S.TicketTitle>{item.product_name}</S.TicketTitle>
                        </Link>
                        {!!activity && (
                            <ActivitySubtitle
                                destination={{
                                    name: activity.city_name,
                                    code: activity.locations?.[0]?.state_code || '',
                                }}
                                ratings={activity.rating_stars}
                                rating={activity.rating}
                                reviewsCount={+activity.rate_count}
                                oneRow
                                hideDestination
                            />
                        )}
                        {(arrivalDate || arrivalTime) && (
                            <S.TicketDate>
                                {' '}
                                {arrivalDate} @ {arrivalTime}
                            </S.TicketDate>
                        )}
                    </S.InnerWrap>
                </S.TopContainer>
                {(!!activity?.policy_title || isTripflex) && (
                    <Tooltip
                        description={isTripflex ? TripflexTipContent() : activity?.policy_content}
                        inCart={false}
                    >
                        <S.Policy>
                            <Policy />
                            {isTripflex ? TRIPFLEX_TITLE : activity?.policy_title}
                        </S.Policy>
                    </Tooltip>
                )}
            </S.TopWrapper>
            <S.DashedContainer>
                <S.CircleLeft />
                <S.DashedLine>
                    {Array(6)
                        .fill(0)
                        .map((index) => (
                            <S.Divider key={keyId.concat(index)} />
                        ))}
                </S.DashedLine>
                <S.CircleRight />
            </S.DashedContainer>
            <S.BottomWrapper data-test-id="Cart_tickets_types">
                {item.tickets.map(({ name, booked, price, id }) => (
                    <S.TicketAlignment key={id}>
                        <S.TextWrapper>
                            <S.IconWrapper>
                                <Ticket2 />
                            </S.IconWrapper>
                            <S.TicketText>
                                {booked} <S.CrossIcon /> {name}
                            </S.TicketText>
                        </S.TextWrapper>
                        <S.TicketPrice>${(+price * +booked).toFixed(2)}</S.TicketPrice>
                    </S.TicketAlignment>
                ))}
                {item.additional_options.map(({ id, name, value }) => {
                    return (
                        <S.TicketAlignment key={id}>
                            <S.TextWrapper>
                                <S.IconOptionWrapper>
                                    {id === TRIPFLEX_ID ? <TripflexIcon /> : <OptionIcon />}
                                </S.IconOptionWrapper>
                                <S.TicketText>
                                    {name} ({value.name})
                                </S.TicketText>
                            </S.TextWrapper>
                            <S.TicketPrice>${Number(value.price).toFixed(2)}</S.TicketPrice>
                        </S.TicketAlignment>
                    );
                })}
                <S.BottomTicket>
                    <S.LeftBottomContainer>
                        <S.EventLink onClick={() => displayModal()}>
                            <S.EditIcon />
                            &nbsp;Edit
                        </S.EventLink>
                        <ResponseEditModal
                            setShow={setShow}
                            show={show}
                            edit={() => editTicket(item)}
                        />
                        <S.EventLink onClick={() => removeItem(item)}>
                            <S.RemoveIcon />
                            &nbsp;Remove
                        </S.EventLink>
                    </S.LeftBottomContainer>
                    <S.RightBottomContainer>
                        {!!itemTotals.discount && (
                            <S.SubTotalPromoText>
                                Promo Code:{' '}
                                <S.SubTotalPromo>- ${itemTotals.discountString}</S.SubTotalPromo>
                            </S.SubTotalPromoText>
                        )}
                        <S.SubTotalText>
                            Subtotal:{' '}
                            <S.SubTotal>${formatSubunits(item.subtotal_subunits)}</S.SubTotal>
                        </S.SubTotalText>
                    </S.RightBottomContainer>
                </S.BottomTicket>
            </S.BottomWrapper>
        </S.Container>
    );
};

export const CartItem = memo(CartItemContainer);
